import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  createBookEdition,
  createBookType,
  createSpecimen,
  deleteSpecimen,
  productQueryKeys,
  updateBookEdition,
  updateBookType,
  updateSpecimen,
} from '../api/product.api';
import type { BookEditionPayload, BookTypePayload, SpecimenPayload } from '../types/product.types';

interface SaveArgs<TPayload> {
  id: number | null;
  payload: TPayload;
}

function useInvalidateCatalogue() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: productQueryKeys.all() });
  };
}

export function useSaveBookType(onSuccess?: () => void) {
  const invalidate = useInvalidateCatalogue();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<BookTypePayload>) =>
      id === null ? createBookType(payload) : updateBookType(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Book type created' : 'Book type updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the book type'),
  });
}

export function useSaveBookEdition(onSuccess?: () => void) {
  const invalidate = useInvalidateCatalogue();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<BookEditionPayload>) =>
      id === null ? createBookEdition(payload) : updateBookEdition(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Book edition created' : 'Book edition updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the book edition'),
  });
}

export function useSaveSpecimen(onSuccess?: () => void) {
  const invalidate = useInvalidateCatalogue();

  return useMutation({
    mutationFn: ({ id, payload }: SaveArgs<SpecimenPayload>) =>
      id === null ? createSpecimen(payload) : updateSpecimen(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Specimen created' : 'Specimen updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the specimen'),
  });
}

export function useDeleteSpecimen(onSuccess?: () => void) {
  const invalidate = useInvalidateCatalogue();

  return useMutation({
    mutationFn: (id: number) => deleteSpecimen(id),
    onSuccess: () => {
      invalidate();
      toast.success('Specimen removed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to remove the specimen'),
  });
}
